import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import {
  SearchBar,
  useLanguageTranslator,
  StatusTag,
} from "../../../components";
import { gql } from "@apollo/client";
import { useApolloQuery } from "../../../hooks";
import { useCallback, useMemo, useState } from "react";
import TabLabel from "../../../components/@common/TabLabel";
import { LeftArrowIcon, RightArrowIcon } from "../../../@lib/icons";
import dayjs from "dayjs";

const GET_SUBMISSIONS = gql`
  query GetSubmissions(
    $after: String
    $first: Int
    $before: String
    $last: Int
  ) {
    submissions(after: $after, first: $first, before: $before, last: $last) {
      edges {
        cursor
        node {
          __typename
          ... on Policy {
            title
            description
            status
            createdAt
          }
          ... on Practice {
            title
            description
            status
            createdAt
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
        startCursor
      }
      totalCount
    }
  }
`;

function AllSubmissions() {
  const [inputValue, setInputValue] = useState("");
  const [currTab, setCurrTab] = useState<"all">("all");
  const [nextItemsView, SetNextItemsView] = useState<any>({
    first: 8,
  });

  const { translator } = useLanguageTranslator();

  const { data, loading, refetch }: any = useApolloQuery({
    queryDocument: GET_SUBMISSIONS,
    variables: {
      ...nextItemsView,
    },
    isPrivateCall: true,
  });

  const dist = useMemo(() => {
    const dist = {
      approved: [] as any,
      rejected: [] as any,
      pending: [] as any,
    };

    data?.submissions?.edges?.forEach((p: any) => {
      switch (p?.node?.status) {
        case "rejected":
          dist.rejected.push(p);
          break;
        case "approved":
          dist.approved.push(p);
          break;
        case "pending":
        default:
          dist.pending.push(p);
          break;
      }
    });

    return dist;
  }, [data?.submissions?.edges]);

  const privateTab = [
    {
      value: "all",
      count: data?.submissions?.edges?.length,
      title: translator("All"),
    },
    {
      value: "approved",
      count: dist.approved.length,
      title: translator("status.Approved"),
    },
    {
      value: "pending",
      count: dist.pending.length,
      title: translator("status.Pending"),
    },
    {
      value: "rejected",
      count: dist.rejected.length,
      title: translator("status.Rejected"),
    },
  ];

  const submissionsData = (() => {
    switch (currTab as any) {
      case "approved":
        return dist.approved;
      case "pending":
        return dist.pending;
      case "rejected":
        return dist.rejected;
      default:
        return data?.submissions?.edges || [];
    }
  })();

  const filteredSubmissionsList = useMemo(() => {
    if (!inputValue) return submissionsData || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return submissionsData?.filter((item: any) =>
      item?.node?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, submissionsData]);

  const onClickNextPagination = useCallback(() => {
    if (data?.submissions?.pageInfo?.hasNextPage) {
      SetNextItemsView((prev: any) => ({
        first: 8,
        after: data?.submissions?.pageInfo?.endCursor,
      }));
    }
  }, [data?.submissions?.pageInfo]);

  const onClickBackPagination = useCallback(() => {
    if (data?.submissions?.pageInfo?.hasPreviousPage) {
      SetNextItemsView((prev: any) => ({
        last: 8,
        before: data?.submissions?.pageInfo?.startCursor,
      }));
    }
  }, [data?.submissions?.pageInfo]);

  return (
    <Container sx={{ my: 3 }}>
      <Paper component={Container} sx={{ py: 3, mb: 2 }}>
        <Box mb={2}>
          <Typography variant="h4">{translator("Submissions Tab")}</Typography>
          <Typography variant="caption">
            {translator(
              "Categorized view of all the submissions you have made"
            )}
            .
          </Typography>
        </Box>

        <Stack
          direction={{ lg: "row" }}
          gap={2}
          justifyContent={{ lg: "space-between" }}
          alignItems={{ lg: "center" }}
        >
          <Tabs
            value={currTab}
            onChange={(_, val) => setCurrTab(val)}
            variant="scrollable"
            sx={{ my: { xs: 1, lg: 0, maxWidth: "1000px" } }}
          >
            {privateTab?.map(({ value, count, title }: any) => (
              <Tab
                value={value}
                label={<TabLabel count={count}>{title}</TabLabel>}
              />
            ))}
          </Tabs>

          <Box display={{ xs: "none", lg: "block" }}>
            <SearchBar
              placeholder={translator("Search your submissions")}
              setInputValue={setInputValue}
              inputValue={inputValue}
            />
          </Box>
        </Stack>
      </Paper>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ height: "52dvh", overflowY: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>
                  {translator("dashboardTableHead.Category")}
                </TableCell>
                <TableCell>{translator("dashboardTableHead.Name")}</TableCell>
                <TableCell>
                  {translator("dashboardTableHead.Description")}
                </TableCell>
                <TableCell>{translator("dashboardTableHead.Status")}</TableCell>
                <TableCell align="right">
                  {translator("dashboardTableHead.Uploaded date")}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredSubmissionsList?.map((elem: any, i: any) => (
                <TableRow key={`${i}`}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{elem?.node?.__typename}</TableCell>
                  <TableCell title={elem?.node?.title}>
                    {elem?.node?.title?.length > 29
                      ? `${elem?.node?.title?.slice(0, 29)}...`
                      : elem?.node?.title}
                  </TableCell>
                  <TableCell>{elem?.node?.description || "-"}</TableCell>
                  <TableCell padding="checkbox">
                    <StatusTag
                      label={
                        elem?.node?.status?.charAt(0)?.toUpperCase() +
                        elem?.node?.status?.slice(1)
                      }
                      status={elem?.node?.status}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {dayjs(elem?.node?.createdAt?.split("T")?.[0] || "").format(
                      "DD.MM.YYYY"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction="row" justifyContent="center" my={2}>
          <Button
            style={{
              color: "#979da7",
              cursor: data?.submissions?.pageInfo?.hasPreviousPage
                ? "pointer"
                : "not-allowed",
              opacity: data?.submissions?.pageInfo?.hasPreviousPage ? 1 : 0.5,
            }}
            disabled={!data?.submissions?.pageInfo?.hasPreviousPage}
            onClick={onClickBackPagination}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <LeftArrowIcon
                fill={
                  !data?.submissions?.pageInfo?.hasPreviousPage
                    ? "#344054"
                    : "#F6A000"
                }
              />
            </div>
          </Button>
          <Button
            style={{
              color: "#979da7",
              marginLeft: "10px",
              cursor: data?.submissions?.pageInfo?.hasNextPage
                ? "pointer"
                : "not-allowed",
              opacity: data?.submissions?.pageInfo?.hasNextPage ? 1 : 0.5,
            }}
            disabled={!data?.submissions?.pageInfo?.hasNextPage}
            onClick={onClickNextPagination}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <RightArrowIcon />
            </div>
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

export { AllSubmissions };
